import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { ComponentPropsWithoutRef } from 'react'

import { DatoGatsbyImage } from '@/features/common/'
import { DatoStructuredText } from '@/features/common/'
import { DatoLinkIcon } from '@/features/dato-link'
import { useLocalFileUrl } from '@/hooks/useLocalFileUrl'
import { bezier, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentPropsWithoutRef<any> {
  data?: Queries.NewsletterFragment | null
  latestHeading:
    | NonNullable<
        Queries.NewsletterPageQuery['datoCmsNewsletterPage']
      >['latestHeading']
    | null
    | undefined
  layout?: 'FEATURED' | 'DEFAULT'
}

export const Newsletter = ({
  data,
  layout = 'DEFAULT',
  latestHeading,
  ...props
}: Props): JSX.Element => {
  const pdfUrl = useLocalFileUrl(data?.pdf?.localFileId)
  const Link = ({
    children,
    ...props
  }: ComponentPropsWithoutRef<'a'>) => {
    return (
      <a
        href={
          data?.pdfOrUrl === 'PDF' && pdfUrl
            ? pdfUrl + '#page=1&view=Fit'
            : data?.pdfOrUrl === 'URL' && data.url
              ? data.url
              : undefined
        }
        target="_blank"
        rel="noreferrer"
        {...props}
      >
        {children}
      </a>
    )
  }
  const Element = layout === 'DEFAULT' ? Link : 'div'

  const styles = {
    container: css`
      position: relative;
      display: grid;
      align-content: flex-start;
      text-decoration: none;
      ${layout === 'FEATURED' &&
      css`
        grid-column: 1 / -1;
        grid-template-columns: 1fr 2fr;
        grid-column-gap: var(--margin);
        align-items: center;
        padding-bottom: var(--row-36);
        ${mq().ms} {
          grid-template-columns: 1fr;
        }
      `}
      ${layout === 'DEFAULT' &&
      css`
        --border-thickness: 1rem;
        &:before {
          content: '';
          position: absolute;
          top: calc(-1 * var(--border-thickness));
          left: calc(-1 * var(--border-thickness));
          width: calc(100% + 2 * var(--border-thickness));
          height: calc(100% + 2 * var(--border-thickness));
          background: rgba(0, 0, 0, 0);
          transition: background-color 500ms ${bezier.easeOut};
        }
        @media (hover: hover) {
          &:hover:before {
            background: rgba(0, 0, 0, 0.05);
          }
        }
      `}
    `,
    image: css`
      width: 100%;
      overflow: hidden;
      ${layout === 'DEFAULT' &&
      css`
        [data-gatsby-image-wrapper] {
          transition: transform 500ms ease;
        }
        @media (hover: hover) {
          a:hover & {
            [data-gatsby-image-wrapper] {
              transform: scale3d(1.05, 1.05, 1);
            }
          }
        }
      `}
      ${layout === 'FEATURED' &&
      css`
        align-self: flex-start;
        ${mq().ms} {
          order: 2;
          margin-top: 1em;
        }
      `}
    `,
    text: css`
      ${layout === 'DEFAULT' &&
      css`
        position: relative;
        padding: 1em 0.5em 1em 1.25em;
        margin-top: 0.75rem;
        border-left: 2px solid ${colors.gray90};
        color: ${colors.gray10};
        transition: color 300ms ease;
        @media (hover: hover) {
          a:hover & {
            color: ${colors.red};
          }
        }
        ${mq().ms} {
          margin-top: 0.5rem;
          padding: 0.5em 0.5em 0.75em 0.5em;
        }
      `}
    `,
    eyebrow: css`
      display: block;
      text-transform: uppercase;
      color: ${colors.gray65};
      margin-bottom: 0.5em;
    `,
    heading: css`
      margin: 0;
      ${layout === 'FEATURED' &&
      css`
        font-size: var(--fs-48);
      `}
      ${layout === 'DEFAULT' &&
      css`
        font-size: var(--fs-24);
      `}
    `,
    description: css`
      max-width: 85ch;
    `,
    link: css`
      position: relative;
      --font-weight: 500;
      font-weight: var(--font-weight);
      display: block;
      margin: 1.5em 0 1.5em;
      padding: 0.75em 1.5em 0.75em 0.75em;
      max-width: fit-content;
      text-transform: uppercase;
      text-decoration: none;
      color: ${colors.red};
      border: 2px ${colors.red} solid;
      transition: color 200ms ease;
      line-height: 1;
      span {
        position: relative;
      }
      &:before {
        content: '';
        position: absolute;
        height: 100%;
        width: 0%;
        top: 0;
        left: 0;
        background: ${colors.red};
        transition: width 200ms ${bezier.easeOut};
      }
      @media (hover: hover) {
        &:hover {
          color: #fff;
          &:before {
            width: 100%;
          }
          && svg {
            transform: translate3d(0, 0, 0);
          }
        }
      }
    `,
  }

  return (
    <Element
      css={styles.container}
      {...props}
    >
      <div css={styles.image}>
        <DatoGatsbyImage
          image={
            layout === 'FEATURED'
              ? data?.thumbnailImage?.featured
              : data?.thumbnailImage?.gatsbyImageData
          }
          alt={data?.thumbnailImage?.alt || ''}
        />
      </div>
      <div css={styles.text}>
        {layout === 'FEATURED' && (
          <span css={styles.eyebrow}>{latestHeading}</span>
        )}
        <h2 css={styles.heading}>{data?.title}</h2>
        {layout === 'FEATURED' && (
          <div css={styles.description}>
            <DatoStructuredText data={data?.description} />
            <Link css={styles.link}>
              <span>Read Now</span>
              <DatoLinkIcon iconType="DOWNLOAD" />
            </Link>
          </div>
        )}
      </div>
    </Element>
  )
}

export const NewsletterFragment = graphql`
  fragment Newsletter on DatoCmsNewsletter {
    id: originalId
    __typename
    thumbnailImage {
      featured: gatsbyImageData(
        width: 600
        sizes: "(max-width: 720px) 95vw, 33vw"
        imgixParams: { ar: "5:6", fit: "crop", crop: "focalpoint" }
      )
      gatsbyImageData(
        width: 600
        sizes: "(max-width: 540px) 50vw, (max-width: 900px) 33vw, 25vw"
        imgixParams: { ar: "5:6", fit: "crop", crop: "focalpoint" }
      )
      ...ImageFocalData
    }
    title
    description {
      value
    }
    pdfOrUrl
    pdf {
      localFileId
    }
    url
  }
`
